/** Persisted Sales Console list filters (tab + channel), shared between the
 * contact list and the multi-chat grid (auto-open respects the channel). */

export type ChannelFilter = '' | 'WHATSAPP' | 'SMS' | 'EMAIL' | 'WEB_WIDGET';
export type OwnerFilter = 'active' | 'all' | 'unclaimed';

export const CHANNEL_OPTIONS: { value: ChannelFilter; label: string }[] = [
  { value: '', label: 'Channel: any' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'WEB_WIDGET', label: 'Web widget' },
];

const FILTERS_KEY = 'sales-console-filters';

export interface SavedFilters {
  ownerFilter: OwnerFilter;
  channelFilter: ChannelFilter;
  /** Action-needed filter: only glowing chats nobody has cleared yet. */
  activeTools: boolean;
  /** Assigned-user filter ('' = anyone). */
  assigneeFilter: string;
  /** Selected tags, at most one per type; a contact must carry all of them. */
  tagFilters: string[];
}

export const FILTER_DEFAULTS: SavedFilters = {
  ownerFilter: 'active',
  channelFilter: '',
  activeTools: false,
  assigneeFilter: '',
  tagFilters: [],
};

/** Validate an untrusted partial (localStorage or server default) into filters. */
export const sanitizeFilters = (parsed: Partial<SavedFilters>): SavedFilters => {
  // Migration: the pre-per-type shape stored a single `tagFilter` string.
  const legacyTag = (parsed as { tagFilter?: unknown }).tagFilter;
  const tagFilters = Array.isArray(parsed.tagFilters)
    ? parsed.tagFilters.filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
    : typeof legacyTag === 'string' && legacyTag.trim()
      ? [legacyTag.trim()]
      : [];
  return {
    ownerFilter:
      parsed.ownerFilter === 'all' || parsed.ownerFilter === 'unclaimed'
        ? parsed.ownerFilter
        : 'active',
    channelFilter: CHANNEL_OPTIONS.some((o) => o.value === parsed.channelFilter)
      ? (parsed.channelFilter as ChannelFilter)
      : '',
    activeTools: parsed.activeTools === true,
    assigneeFilter: typeof parsed.assigneeFilter === 'string' ? parsed.assigneeFilter : '',
    tagFilters,
  };
};

/** True once this browser has saved its own filters (defaults no longer apply). */
export const hasStoredFilters = (): boolean => localStorage.getItem(FILTERS_KEY) !== null;

/** Parse the SALES_CONSOLE_DEFAULT_FILTERS setting value (empty/invalid → null). */
export const parseDefaultFilters = (raw: string | null | undefined): SavedFilters | null => {
  if (!raw) return null;
  try {
    return sanitizeFilters(JSON.parse(raw) as Partial<SavedFilters>);
  } catch {
    return null;
  }
};

export const getSavedFilters = (): SavedFilters => {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    if (!raw) return FILTER_DEFAULTS;
    return sanitizeFilters(JSON.parse(raw) as Partial<SavedFilters>);
  } catch {
    return FILTER_DEFAULTS;
  }
};

export const saveFilters = (filters: SavedFilters) => {
  localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
};
